import { PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';
import { Trans, useTranslation } from '@packlink/translation-provider';
import { IconNames } from '@shipengine/giger-theme';
import { Button, Divider, Icon, IconSize, Next, Typography } from '@shipengine/giger';
import { APP_ROUTE } from '@pages/router/routes';
import { AmplitudeEvents } from '@constants/amplitude';
import {
    NotificationBody,
    SubscriptionNotification,
} from '@common/components/SubscriptionNotification/SubscriptionNotification';
import { SubHeader } from '@BrandedEmails/components/Header/SubHeader';
import { BaseHeader } from '../../components/Header/BaseHeader';
import {
    getContentStyles,
    getIconStyles,
    getLinkStyles,
    getMainStyles,
    getEmailHeaderStyles,
    getEmailContentStyles,
    getGreetingStyles,
    getHeaderLabelStyles,
    getContentBlockStyles,
    getRowStyles,
    getMessageStyles,
    getDateStyles,
    getButtonWrapperStyles,
    getDividerStyles,
    getPoweredStyles,
    getLogosStyles,
    getLogoStyles,
    getButtonStyles,
} from './DefaultEmailStyles';

export function DefaultEmail(): JSX.Element {
    const {
        cdn: { url: cdnUrl },
    } = config;
    const {
        t,
        i18n: { language: locale },
    } = useTranslation();
    const navigate = useNavigate();

    const goBack = () => {
        navigate(APP_ROUTE.SETTINGS.BRANDED_EMAILS);
    };

    return (
        <>
            <BaseHeader>
                <Next.Link onClick={goBack} css={getLinkStyles}>
                    <Icon css={getIconStyles} name={IconNames.CHEVRON_LEFT} size={IconSize.SIZE_SMALL} />
                    <Typography variant="body2" bold>
                        {t('branded-emails.header.go-back')}
                    </Typography>
                </Next.Link>
            </BaseHeader>

            <SubHeader
                title={`${t('branded-emails.label.preview')}: ${t('branded-emails.template-list.shipment-confirmation-title')}`}
            ></SubHeader>

            <main css={getMainStyles}>
                <section css={getContentStyles}>
                    <SubscriptionNotification>
                        <NotificationBody
                            title={t('subscriptions.upgrade-notification.default-email')}
                            amplitudeEvent={`${AmplitudeEvents.SETTINGS_TOOLTIP_BRANDED_EMAIL} default email`}
                        >
                            {t('subscriptions.upgrade-notification.default-email-message')}
                        </NotificationBody>
                    </SubscriptionNotification>

                    <section css={getEmailHeaderStyles}>
                        <div>
                            <Typography variant="body2" component="label" bold css={getHeaderLabelStyles}>
                                {t('branded-emails.default-email.from')}:
                            </Typography>
                            <Typography variant="body2" component="span">
                                noreply@packlink.com
                            </Typography>
                        </div>

                        <div>
                            <Typography variant="body2" component="label" bold css={getHeaderLabelStyles}>
                                {t('branded-emails.default-email.subject')}:
                            </Typography>
                            <Typography variant="body2" component="span">
                                {t('branded-emails.default-email.subject-message')}
                            </Typography>
                        </div>
                    </section>

                    <section css={getEmailContentStyles}>
                        <Typography variant="heading3" component="span" css={getGreetingStyles}>
                            {t('branded-emails.default-email.greeting')},
                        </Typography>

                        <Typography variant="body2" component="p" css={getMessageStyles}>
                            {t('branded-emails.default-email.products-sent')}
                        </Typography>

                        <div css={getRowStyles}>
                            <ContentBlock label={t('branded-emails.default-email.delivery-date')}>
                                <span css={getDateStyles}>
                                    {Intl.DateTimeFormat(locale, {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric',
                                    }).format(new Date())}
                                </span>
                            </ContentBlock>

                            <ContentBlock label={t('branded-emails.default-email.delivery-address')}>
                                <div>{t('branded-emails.default-email.address-name')}</div>
                                <div>{t('branded-emails.default-email.address-street')}</div>
                            </ContentBlock>
                        </div>

                        <div css={getRowStyles}>
                            <ContentBlock label={t('branded-emails.default-email.tracking-number')}>
                                R000A41C023148288T
                            </ContentBlock>

                            <ContentBlock label={t('branded-emails.default-email.shipment-reference')}>
                                ES2025PRO0000000123
                            </ContentBlock>
                        </div>

                        <div css={getButtonWrapperStyles}>
                            <Button css={getButtonStyles}>{t('branded-emails.default-email.shipment-tracking')}</Button>
                        </div>

                        <Divider css={getDividerStyles} />

                        <div css={getPoweredStyles}>
                            <Typography variant="small" component="span" bold>
                                This shipment was powered by
                            </Typography>

                            <div css={getLogosStyles}>
                                <img
                                    alt="Packlink Pro"
                                    css={getLogoStyles}
                                    src={`${cdnUrl}/giger/logos/packlink-pro.svg`}
                                />
                                <img alt="Packlink" css={getLogoStyles} src={`${cdnUrl}/giger/logos/packlink.svg`} />
                            </div>
                        </div>

                        <Typography variant="small" component="span">
                            <Trans
                                i18nKey={'branded-emails.default-email.footer-text'}
                                components={[<Next.Link key="link" />]}
                            />
                        </Typography>
                    </section>
                </section>
            </main>
        </>
    );
}

type ContentBlockProps = {
    label: string;
};
function ContentBlock({ label, children }: PropsWithChildren<ContentBlockProps>) {
    return (
        <section css={getContentBlockStyles}>
            <Typography variant="body2" component="label" bold>
                {label}
            </Typography>

            <Typography variant="body2" component="span">
                {children}
            </Typography>
        </section>
    );
}
